import Component from "@ember/component"
import { action, computed, get, set } from "@ember/object"
import { join, scheduleOnce } from "@ember/runloop"
import { inject as service } from "@ember/service"
import HydratedDate from "@shared/models/hydrated-date"
import HydratedUnit from "@shared/models/hydrated-unit"
import ObjectId from "@shared/util/object-id-gen"
import actions from "cc-frontend/lib/actions/course-calendar"
import RESIZE_UNIT from "cc-frontend/lib/actions/course/calendar/RESIZE_UNIT"
import { applyOps } from "cc-frontend/lib/ember-patch"
import _ from "lodash"
import { find } from "lodash-es"

let calculateMovementAmount = function (clientX, initialClientX, dateWidth) {
  let dx = clientX - initialClientX
  return dx < 0 ? Math.ceil(dx / dateWidth) : Math.floor(dx / dateWidth)
}

let accountForDaysOffInMovement = function (amount, unitId, dates, units) {
  let unit = _.find(units, { id: unitId })
  let endDateIndex = _.findIndex(dates, (date) => date.dateString === unit.endDate)
  let addOrSub = amount < 0 ? (num) => num + 1 : (num) => num - 1
  return _.reduce(
    _.range(endDateIndex, endDateIndex + amount),
    (acc, index) => {
      if (dates[index] && dates[index].isOff) acc = addOrSub(acc)
      return acc
    },
    amount
  )
}

export default class CourseTimelineUnitsAndLessons extends Component {
  @service("ember-store")
  store

  @service("dispatcher")
  dispatcher

  @service
  appState

  @service
  modalManager

  wasMovingLesson = false

  init() {
    super.init()
    this.hideCourseTitleBanner = () => {}
    this.clickedDate = () => {}
    this.cachedStyles = {}
    this.renderCount = -1
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments)
    set(this, "courseClone", _.cloneDeep(get(this, "course")))
    join(() => {
      this.setUnitsAndLessons(get(this, "course"), true, [], [])
    })
  }

  /**
    ON SETTING THE STYLE
      * If we concat the style and set it as "style=" in the component, we cut the
        rerender down from 75-80ms to 45-50ms.

    ON UPDATE STRATEGIES
      * If we replace the dates/units, it takes 45-50ms to update all the units/lessons
      * If we do an Ember.set on the dates/units, it takes 25-30ms to update all
        the units/lessons.
      * For historical reasons, here's the code to reset the dates
        ```js
        // UNITS
        _.each(date.unitStart, id => unitMap[id] = _.assign({}, unitMap[id], {startIndex: acc.index + counter}))
        _.each(date.unitEnd, id => {
          let unit = unitMap[id]
          let style = `transform: translate3d(${unit.startIndex * dateWidth}px, ${barHeight * unit.ancestorIds.length + lessonBarHeight + courseTitleHeight}px, 0);
              width: ${unit.length * dateWidth}px;
              height: ${barHeight}px;`
          unitMap[id] = _.assign(unitMap[id], {
            endIndex: acc.index + counter,
            style: style
          })
        })

        // DATES
        acc.dates.push(_.assign({}, date, {index: acc.index + counter, style: style}))
        ```
    ON CACHING THE STYLES
    We cache the styles so recalc styles decreases. By caching, we drop the time
    spent recalculating the style by about 40%.

    ON STORING THE RENDER COUNT
    This is confusing. I'm sorry future self. The first time we render (when we're)
    not replacing the dates, we want to store the first render and then just store
    the diffs aftewrards. This is why we check this.renderCount whenever
    we're about to set or check the cache.
   */

  setUnitsAndLessons(course, replaceDates, cardStackIdsOnTheMove, unitIdsOnTheMove) {
    // if (replaceDates) {
    //   this.renderCount = -1;
    // }
    // if (this.renderCount < 2) this.cachedStyles = {};
    // this.renderCount++
    if (replaceDates) {
      this.renderCount = -1
      this.cachedStyles = {}
    }
    this.renderCount++

    // Models
    let dates = get(this, "dates") // this needs to be renamed

    let units = get(course, "attributes.calendar.units")
    let unitMap = _.keyBy(units, "id")
    let cardStackIdsOnTheMoveMap = _.reduce(
      cardStackIdsOnTheMove,
      (acc, id) => _.set(acc, id, true),
      {}
    )
    let unitIdsOnTheMoveMap = _.reduce(unitIdsOnTheMove, (acc, id) => _.set(acc, id, true), {})

    // Sizes
    let dateWidth = get(this, "dateWidth")
    let lessonBarHeight = get(this, "lessonBarHeight")
    let courseTitleHeight = get(this, "courseTitleHeight")
    let barHeight = get(this, "barHeight")

    let currentDatesByCardStackId = _.keyBy(get(this, "hydratedDates"), "cardStackId")

    // Depending on how we're rendering, choose which array to access
    // get(this, 'units') was previously the currentUnitsByUnitId
    let unitsToSet
    if (replaceDates) {
      unitsToSet = _.chain(get(course, "attributes.calendar.units"))
        .map((unit) => {
          return new HydratedUnit({
            id: unit.id,
            startDate: unit.startDate,
            endDate: unit.endDate,
            ancestorIds: unit.ancestorIds,
            descendantIds: unit.descendantIds,
            unitLength: unit.unitLength,
            depth: unit.ancestorIds.length,
          })
        })
        .keyBy("id")
        .value()
      // Make sure all the units are there.
      _.map(
        _.filter(
          get(course, "attributes.calendar.dates"),
          (date) => date.type === "course-date-custom"
        ),
        (date) => {
          let unitIds = _.concat([], date.attributes.unitStart, date.attributes.unitEnd)
          _.map(unitIds, (id) => {
            if (unitsToSet[id] === undefined) {
              console.log(`Unit ${id} wasn't found in calendar.units of course ${course.id}.`)
              unitsToSet[id] = new HydratedUnit({
                id: id,
                startDate: null,
                endDate: null,
                ancestorIds: [],
                descendantIds: [],
                unitLength: 1,
                depth: 1,
              })
            }
          })
        }
      )
    } else {
      unitsToSet = _.keyBy(get(this, "hydratedUnits"), "id")
    }

    // use unit util library -- we need the unitIds for each date to properly
    // position the "Add Unit" button
    // set the dates
    // set the units
    // if updating
    // update the dates, update the units

    let translate = function (is2d, x, y) {
      if (is2d) {
        return `translate(${x}px, ${y}px)`
      } else {
        return `translate3d(${x}px, ${y}px, 0)`
      }
    }

    // By adding all the styles together and inserting them in a style tag,
    // we're able to rather significantly reduce the number of updates
    // Ember has to do. This seems to reduce the update by 25%ish and the
    // browser seems to process it just as fast.
    let styleToInsert = ""
    let i = -1
    let indexedDateStrings = _.reduce(
      dates,
      (acc, date) => {
        i++
        acc[date.dateString] = i
        return acc
      },
      {}
    )

    let firstDate = _.get(_.first(dates), "dateString")

    let results = _.chain(get(course, "attributes.calendar.dates"))
      .filter((cd) => cd.type === "course-date-custom")
      .sortBy("attributes.dateString")
      .reduce(
        (acc, courseDate) => {
          // Set index: If we're in the dates displayed, use that index. Otherwise,
          // use 0 if we're before the first date and the length of the dates
          // if we're after the last date
          let index = indexedDateStrings[courseDate.attributes.dateString]
            ? indexedDateStrings[courseDate.attributes.dateString]
            : courseDate.attributes.dateString < firstDate
              ? 0
              : dates.length

          _.forEach(courseDate.attributes.unitStart, (id) => {
            // Make sure it exists
            if (unitsToSet[id] === undefined) {
              console.log(`Unit ${id} isn't in the units map for course ${course.id}.`)
              throw new Error(`Unit ${id} isn't in the units map for course ${course.id}.`)
            }
            set(unitsToSet[id], "startIndex", index)
          })

          // if (date && (date.cardStackId || _.size(date.unitStart) > 0 || _.size(date.unitEnd) > 0)) {
          if (courseDate.attributes.cardStackId) {
            // let scale = isMoving ? 1 : 1;
            if (replaceDates) {
              let hydratedDate = new HydratedDate({
                dateString: courseDate.attributes.dateString,
                cardStackId: courseDate.attributes.cardStackId,
                unitStart: courseDate.attributes.unitStart,
                unitEnd: courseDate.attributes.unitEnd,
                unitIds: _.clone(courseDate.attributes.unitIds),
                index: index,
                isMoving: false,
              })
              acc.dates.push(hydratedDate)
            } else {
              set(
                currentDatesByCardStackId[courseDate.attributes.cardStackId],
                "isMoving",
                cardStackIdsOnTheMoveMap[courseDate.attributes.cardStackId]
              )
            }

            let translateString = translate(replaceDates, index * dateWidth, 0)
            let lessonStyle = `
          #date-lesson-id-${courseDate.attributes.cardStackId}{ transform: ${translateString}; width: ${dateWidth}px; }
        `
            if (
              replaceDates ||
              this.renderCount < 2 ||
              this.cachedStyles[courseDate.attributes.cardStackId] !== lessonStyle
            ) {
              styleToInsert = styleToInsert + lessonStyle
              if (replaceDates || this.renderCount < 2)
                this.cachedStyles[courseDate.attributes.cardStackId] = lessonStyle
            }
          }

          _.forEach(courseDate.attributes.unitEnd, (id) => {
            if (unitsToSet[id] === undefined) {
              console.log(`Unit ${id} isn't in the units map for course ${course.id}.`)
              throw new Error(`Unit ${id} isn't in the units map for course ${course.id}.`)
            }
            let depth = unitMap[id] ? unitMap[id].ancestorIds.length : 3
            let unitLength = unitMap[id] ? index + 1 - unitsToSet[id].startIndex : 1
            set(unitsToSet[id], "endIndex", index) // Needed for unit movement
            set(unitsToSet[id], "isMoving", unitIdsOnTheMoveMap[id])
            set(unitsToSet[id], "depth", depth)
            // set(unitToSet, 'style', style)
            let translateString = translate(
              replaceDates,
              unitsToSet[id].startIndex * dateWidth,
              barHeight * depth + lessonBarHeight + courseTitleHeight
            )
            let unitStyle = `
          #unit-${id} { transform: ${translateString}; width: ${
            unitLength * dateWidth
          }px; height: ${barHeight}px; }
        `
            if (replaceDates || this.renderCount < 2 || this.cachedStyles[id] !== unitStyle) {
              styleToInsert = styleToInsert + unitStyle
              if (replaceDates || this.renderCount < 2) this.cachedStyles[id] = unitStyle
            }
          })

          return acc
        },
        { index: -1, dates: [] }
      )
      .value()

    if (replaceDates || this.renderCount < 2) {
      this.timelineCss = styleToInsert
      this.timelineCssUpdate = ""
      this.updateBaseTimelineCss()
      this.updateUpdatedTimelineCss()
    } else {
      this.timelineCssUpdate = styleToInsert
      this.updateUpdatedTimelineCss()
    }

    if (replaceDates) {
      set(this, "hydratedDates", results.dates)
      set(this, "hydratedUnits", _.values(unitsToSet))
    }
  }

  // this is the base css for the whole tieline
  updateBaseTimelineCss() {
    let el = document.getElementById(`timeline-css-for-${this.course.id}`)
    if (el) el.innerHTML = this.timelineCss
  }

  // This is the css we use between drags
  updateUpdatedTimelineCss() {
    let el = document.getElementById(`timeline-css-update-for-${this.course.id}`)
    if (el) el.innerHTML = this.timelineCssUpdate
  }

  didInsertElement() {
    super.didInsertElement(...arguments)
    this.updateBaseTimelineCss()
    this.updateUpdatedTimelineCss()

    if (_.includes(get(this, "course.meta.permissions"), "can-edit-plan") === false) return
    this.initLessonMoveHandlers()
    this.initUnitMoveHandlers()
    this.initResizeRightHandlers()
    this.initResizeLeftHandlers()
  }

  initUnitMoveHandlers() {
    let initialClientX = 0
    let initialTimelineScrollX = 0
    let originalUnitBeingMoved = null
    /**
     *==================================================
     * UNIT MOVEMENT
     *==================================================
     */

    let unitMovement = (event) => {
      // let startTime = new Date()
      let { _top, left } = $(".js-timeline-scroller").offset()
      // We need to take into account how scrolling effects the position. This code does it.
      // the left is a negative number if you scroll left (it's a bit confusing) hence the * -1
      let amount = calculateMovementAmount(
        left * -1 + event.clientX - Math.abs(initialTimelineScrollX),
        initialClientX,
        get(this, "dateWidth")
      )
      let unitId = event.target.getAttribute("data-unit-id")

      // let endTime = new Date()
      // let totalTime = endTime - startTime
      // window._unitMovementCount = window._unitMovementCount || 0
      // window._unitMovementAvg = window._unitMovementAvg || 0
      // window._unitMovementSum = window._unitMovementSum || 0
      // window._unitMovementSum = window._unitMovementSum + totalTime
      // window._unitMovementCount++
      // window._unitMovementAvg = window._unitMovementSum / window._unitMovementCount
      // console.log('avg', window._unitMovementAvg)

      let newAmount = accountForDaysOffInMovement(
        amount,
        unitId,
        get(this, "dates"),
        get(this, "hydratedUnits")
      )

      // Find the new start date by looking at the index of the original start date
      // and adding the amount moved to it. We use the planbookCalendarForAdding because
      // it has the dates that are not in the course calendar (this.dates)
      let startIndex = _.findIndex(
        this.planbookCalendarForAdding,
        (date) => date.attributes.dateString == originalUnitBeingMoved.startDate
      )
      let dateToMoveToIndex = startIndex + amount
      let newStartDate = this.planbookCalendarForAdding[dateToMoveToIndex]

      return {
        unitId: unitId,
        amount: newAmount,
        course: get(this, "course"),
        planbook: get(this, "planbook"),
        newStartDate: newStartDate.attributes.dateString,
        rotationCalendar: get(this, "rotationCalendar"),
      }
    }

    let unitEvents = []
    let drawUnits = () => {
      if (this.unitMovementHasEnded === true) return
      let event = unitEvents.pop()
      if (_.isNil(event)) {
        window.requestAnimationFrame(drawUnits)
        return
      }
      let patchParams = unitMovement(event)
      let patches = actions["MOVE_UNIT"].patches(patchParams)
      let patch = patches[0]
      if (_.isNil(patch)) {
        window.requestAnimationFrame(drawUnits)
        return
      }

      join(() => {
        applyOps(get(this, "courseClone"), patch.document.ops)

        // not sure if it helps to wrap in a run loop. Seems to cause it to render
        this.setUnitsAndLessons(
          get(this, "courseClone"),
          false,
          patch.cardStackIdsOnTheMove,
          patch.unitIdsOnTheMove
        )
        // this.sendAction("highlightDates", patch.highlightedDates)
        // let highlightIndex = _.findIndex(get(this, 'dates'), j)
        let hydratedUnit = _.find(get(this, "hydratedUnits"), {
          id: patchParams.unitId,
        })
        this.highlightDates({
          index: hydratedUnit.startIndex,
          width: hydratedUnit.unitLength,
        })
      })
      window.requestAnimationFrame(drawUnits)
    }
    let onUnitMove = _.throttle(
      (event) => {
        if (originalUnitBeingMoved === null) {
          originalUnitBeingMoved = _.find(get(this, "hydratedUnits"), {
            id: event.target.getAttribute("data-unit-id"),
          })
        }
        unitEvents.push(event)
      },
      60,
      { trailing: true }
    )

    let onUnitEnd = (event) => {
      // This stops the drawUnit code
      this.unitMovementHasEnded = true
      get(this, "dispatcher").dispatch("MOVE_UNIT", unitMovement(event))
      originalUnitBeingMoved = null
      this.highlightDates(false)
      this.hideCourseTitleBanner(false)
      this.unitLastMoved = Date.now()
    }

    interact(".js-draggable-unit-block-" + get(this, "course.id")).draggable({
      inertia: false,
      autoScroll: {
        container: $(".js-timeline-wrapper")[0],
        enabled: true,
      },
      onstart: (event) => {
        this.unitMovementHasEnded = false
        let { _top, left } = $(".js-timeline-scroller").offset()
        initialClientX = event.clientX
        initialTimelineScrollX = left
        this.hideCourseTitleBanner(true)
        drawUnits()
      },
      onmove: onUnitMove,
      onend: onUnitEnd,
    })
  }

  initLessonMoveHandlers() {
    let initialClientX = 0
    let initialTimelineScrollX = 0
    /**
     *==================================================
     * LESSON MOVEMENT
     *==================================================
     */

    let lessonMovement = (event) => {
      let { _top, left } = $(".js-timeline-scroller").offset()
      // We need to take into account how scrolling effects the position. This code does it.
      // the left is a negative number if you scroll left (it's a bit confusing) hence the * -1
      let amount = calculateMovementAmount(
        left * -1 + event.clientX - Math.abs(initialTimelineScrollX),
        initialClientX,
        get(this, "dateWidth")
      )
      let originalIndex = _.findIndex(
        get(this, "dates"),
        (date) => date.dateString === event.target.dataset.dateString
      )
      let toDate = get(this, "dates")[originalIndex + amount]
      return {
        cardStackId: event.target.dataset.cardStackId,
        fromDate: event.target.dataset.dateString,
        toDate: toDate.dateString,
        course: get(this, "course"),
        planbook: get(this, "planbook"),
        rotationCalendar: get(this, "rotationCalendar"),
      }
    }
    let lessonEvents = []
    let drawLessons = () => {
      if (this.unitMovementHasEnded === true) return
      let event = lessonEvents.shift()
      if (_.isNil(event)) {
        window.requestAnimationFrame(drawLessons)
        return
      }
      join(() => {
        let patches = actions["MOVE_LESSON_TO_DATE"].patches(lessonMovement(event))
        let patch = patches[0]

        applyOps(get(this, "courseClone"), patch.document.ops)
        this.setUnitsAndLessons(get(this, "courseClone"), false, patch.cardStackIdsOnTheMove)
        this.highlightDates(patch.initialIndexToInsert)
      })
      window.requestAnimationFrame(drawLessons)
    }

    let onLessonMove = _.throttle((event) => {
      lessonEvents.push(event)
    }, 60)

    let onLessonMoveEnd = (event) => {
      this.unitMovementHasEnded = true
      const payload = lessonMovement(event)
      get(this, "dispatcher").dispatch("MOVE_LESSON_TO_DATE", payload)
      this.highlightDates([])

      if (this.modalManager.openedModals.includes("planner/course/timeline/sidebar")) {
        // A lesson, probably this one, is already open in the sidebar.
        // The sidebar will keep showing the old date, which is now a new lesson.
        // Instead update it to show the same lesson at its new date.
        this.openLessonSidebar(payload.toDate, payload.cardStackId, "move")
      }

      // If you mouseup while still over the lesson, it counts as a click event,
      // calling this.openLessonSidebar. Ignore that event, to avoid opening the sidebar
      // and scrolling to the moved lesson.
      this.wasMovingLesson = true
      setTimeout(() => {
        this.wasMovingLesson = false
      }, 10)
    }

    interact(".js-draggable-lesson-circle-" + get(this, "course.id")).draggable({
      inertia: false,
      autoScroll: {
        container: $(".js-timeline-wrapper")[0],
        enabled: true,
      },
      onstart: (event) => {
        this.unitMovementHasEnded = false
        let { _top, left } = $(".js-timeline-scroller").offset()
        initialClientX = event.clientX
        initialTimelineScrollX = left
        drawLessons()
        event.preventDefault()
      },
      onmove: onLessonMove,
      onend: onLessonMoveEnd,
    })
  }

  initResizeRightHandlers() {
    let initialClientX = 0
    let initialTimelineScrollX = 0
    let originalUnitBeingMoved = null

    /**
     *==================================================
     * RESIZING
     *==================================================
     * @returns {{unit: object}}
     */
    let unitResizeMovement = (event) => {
      let { _top, left } = $(".js-timeline-scroller").offset()
      // We need to take into account how scrolling effects the position. This code does it.
      // the left is a negative number if you scroll left (it's a bit confusing) hence the * -1
      let unitId = event.target.getAttribute("data-unit-id")
      let amount = calculateMovementAmount(
        left * -1 + event.clientX - Math.abs(initialTimelineScrollX),
        initialClientX,
        get(this, "dateWidth")
      )

      let newAmount = accountForDaysOffInMovement(
        amount,
        unitId,
        get(this, "dates"),
        get(this, "hydratedUnits")
      )

      // Find the new start date by looking at the index of the original start date
      // and adding the amount moved to it. We use the planbookCalendarForAdding because
      // it has the dates that are not in the course calendar (this.dates)
      let endIndex = _.findIndex(
        this.planbookCalendarForAdding,
        (date) => date.attributes.dateString == originalUnitBeingMoved.endDate
      )
      let dateToMoveToIndex = endIndex + amount
      let newEndDate = this.planbookCalendarForAdding[dateToMoveToIndex]

      return {
        unitId: unitId,
        amount: newAmount,
        newDate: newEndDate.attributes.dateString,
        course: get(this, "course"),
        planbook: get(this, "planbook"),
        startOrEndOfUnit: "END",
        rotationCalendar: get(this, "rotationCalendar"),
      }
    }

    let resizeEvents = []
    let drawUnitResize = () => {
      if (this.unitMovementHasEnded === true) return
      if (originalUnitBeingMoved === null) return window.requestAnimationFrame(drawUnitResize)
      let event = resizeEvents.shift()
      if (_.isNil(event)) {
        window.requestAnimationFrame(drawUnitResize)
        return
      }
      join(() => {
        let patches = RESIZE_UNIT.patches(unitResizeMovement(event))
        if (patches === false) return window.requestAnimationFrame(drawUnitResize)
        let patch = patches[0]
        if (patch === undefined) return window.requestAnimationFrame(drawUnitResize)
        applyOps(get(this, "courseClone"), patch.document.ops)
        this.setUnitsAndLessons(
          get(this, "courseClone"),
          false,
          patch.cardStackIdsOnTheMove,
          patch.unitIdsOnTheMove
        )
      })
      window.requestAnimationFrame(drawUnitResize)
    }
    let onUnitResize = _.throttle((event) => {
      if (originalUnitBeingMoved === null) {
        originalUnitBeingMoved = _.find(get(this, "hydratedUnits"), {
          id: event.target.getAttribute("data-unit-id"),
        })
      }
      resizeEvents.push(event)
    }, 60)

    let onUnitResizeEnd = (event) => {
      this.unitMovementHasEnded = true
      get(this, "dispatcher").dispatch("RESIZE_UNIT", unitResizeMovement(event))
      originalUnitBeingMoved = null
      this.didReceiveAttrs()
      this.unitLastMoved = Date.now()
    }

    interact(".js-resize-unit-" + get(this, "course.id")).draggable({
      inertia: false,
      autoScroll: true,
      onstart: (event) => {
        this.unitMovementHasEnded = false
        let { _top, left } = $(".js-timeline-scroller").offset()
        initialClientX = event.clientX
        initialTimelineScrollX = left
        drawUnitResize()
        event.preventDefault()
      },
      onmove: onUnitResize,
      onend: onUnitResizeEnd,
    })
  }

  initResizeLeftHandlers() {
    let initialClientX = 0
    let initialTimelineScrollX = 0
    let originalUnitBeingMoved = null

    /**
     *==================================================
     * RESIZING
     *==================================================
     * @returns {{unit: object}}
     */
    let unitResizeMovement = (event) => {
      let { _top, left } = $(".js-timeline-scroller").offset()
      // We need to take into account how scrolling effects the position. This code does it.
      // the left is a negative number if you scroll left (it's a bit confusing) hence the * -1
      let unitId = event.target.getAttribute("data-unit-id")
      let amount = calculateMovementAmount(
        left * -1 + event.clientX - Math.abs(initialTimelineScrollX),
        initialClientX,
        get(this, "dateWidth")
      )

      let newAmount = accountForDaysOffInMovement(
        amount,
        unitId,
        get(this, "dates"),
        get(this, "hydratedUnits")
      )

      // Find the new start date by looking at the index of the original start date
      // and adding the amount moved to it. We use the planbookCalendarForAdding because
      // it has the dates that are not in the course calendar (this.dates)
      let startIndex = _.findIndex(
        this.planbookCalendarForAdding,
        (date) => date.attributes.dateString == originalUnitBeingMoved.startDate
      )
      let dateToMoveToIndex = startIndex + amount
      let newStartDate = this.planbookCalendarForAdding[dateToMoveToIndex]

      return {
        unitId: unitId,
        amount: newAmount,
        newDate: newStartDate.attributes.dateString,
        course: get(this, "course"),
        planbook: get(this, "planbook"),
        startOrEndOfUnit: "START",
        rotationCalendar: get(this, "rotationCalendar"),
      }
    }

    let resizeEvents = []
    let drawUnitResize = () => {
      if (this.unitMovementHasEnded === true) return
      if (originalUnitBeingMoved === null) return window.requestAnimationFrame(drawUnitResize)
      let event = resizeEvents.shift()
      if (_.isNil(event)) {
        window.requestAnimationFrame(drawUnitResize)
        return
      }
      join(() => {
        let patches = RESIZE_UNIT.patches(unitResizeMovement(event))
        if (patches === false) return window.requestAnimationFrame(drawUnitResize)
        let patch = patches[0]
        if (patch === undefined) return window.requestAnimationFrame(drawUnitResize)
        applyOps(get(this, "courseClone"), patch.document.ops)
        this.setUnitsAndLessons(
          get(this, "courseClone"),
          false,
          patch.cardStackIdsOnTheMove,
          patch.unitIdsOnTheMove
        )
      })
      window.requestAnimationFrame(drawUnitResize)
    }
    let onUnitResize = _.throttle((event) => {
      if (originalUnitBeingMoved === null) {
        originalUnitBeingMoved = _.find(get(this, "hydratedUnits"), {
          id: event.target.getAttribute("data-unit-id"),
        })
      }
      resizeEvents.push(event)
    }, 60)

    let onUnitResizeEnd = (event) => {
      this.unitMovementHasEnded = true
      get(this, "dispatcher").dispatch("RESIZE_UNIT", unitResizeMovement(event))
      originalUnitBeingMoved = null
      this.didReceiveAttrs()
      this.unitLastMoved = Date.now()
    }

    interact(".js-resize-unit-left-" + get(this, "course.id")).draggable({
      inertia: false,
      autoScroll: true,
      onstart: (event) => {
        this.unitMovementHasEnded = false
        let { _top, left } = $(".js-timeline-scroller").offset()
        initialClientX = event.clientX
        initialTimelineScrollX = left
        drawUnitResize()
      },
      onmove: onUnitResize,
      onend: onUnitResizeEnd,
    })
  }

  @computed("dates", "course.attributes.calendar.version")
  get planbookCalendarForAdding() {
    let courseDatesMap = _.chain(get(this, "course.attributes.calendar.dates"))
      .filter((date) => date.type === "course-date-custom")
      .keyBy("attributes.dateString")
      .value()

    return _.map(get(this, "dates"), (date) => {
      let courseDate = courseDatesMap[date.dateString] || {
        attributes: { cardStackId: null, unitIds: [] },
      }
      return {
        id: date.dateString,
        attributes: {
          dateString: date.dateString,
          cardStackId: courseDate.attributes.cardStackId,
          unitIds: _.clone(courseDate.attributes.unitIds),
        },
      }
      // _.each(courseDate.unitStart, id => currentUnitIds.push(id))
      // let d = _.assign(planbookDate, {unitIds: currentUnitIds.slice(0)})
      // _.pullAll(currentUnitIds, courseDate.unitEnd || [])
      // console.log('date', d)
      // return d
    })
  }

  @action
  openMenu(unit) {
    get(this, "dispatcher").dispatch("SIDEBAR_SET_UNIT_ID", {
      sessionId: get(this, "appState.id"),
      cardStackId: unit.id,
      unitInfo: unit,
      componentId: "lesson-sidebar",
      courseId: get(this, "course.id"),
    })
    scheduleOnce("afterRender", () => {
      $(".unit-popover__unit-title").focus()
    })
  }

  @action
  openLessonSidebar(dateString, cardStackId, action) {
    if (this.wasMovingLesson && action === "click") {
      this.wasMovingLesson = false
      return
    }

    this.modalManager.openModal("planner/course/timeline/sidebar", {
      dateString: dateString,
      cardStackId: cardStackId,
      courseId: this.course.id,
      planbookId: this.planbook.id,
      editCourseTitleAndOrder: this.editCourseTitleAndOrder,
      isUnit: false,
    })

    // When you open a date by clicking it, scroll the timeline to that date.
    if (action === "click") {
      this.clickedDate(dateString)
    }
  }

  @action
  onHighlightDates() {
    this.highlightDates(...arguments)
  }

  @action
  addUnit(dateString) {
    let unitId = ObjectId.create()
    get(this, "dispatcher").dispatch("ADD_UNIT", {
      newUnitId: unitId,
      unitStart: dateString,
      course: get(this, "course"),
      planbook: get(this, "planbook"),
      rotationCalendar: get(this, "rotationCalendar"),
      unitCards: [
        {
          id: ObjectId.create(),
          type: "card-standards",
          attributes: {
            position: 0,
            title: "Standards",
            color: "yellow",
            standards: [],
            attachments: [],
            commentIds: [],
            comments: [],
            isPublic: false,
            parentCardId: null,
          },
        },
      ],
    })

    let newAmount = accountForDaysOffInMovement(
      3,
      unitId,
      get(this, "dates"),
      get(this, "hydratedUnits")
    )

    let endIndex = _.findIndex(
      this.planbookCalendarForAdding,
      (date) => date.attributes.dateString == dateString
    )
    let dateToMoveToIndex = endIndex + newAmount
    let newEndDate = this.planbookCalendarForAdding[dateToMoveToIndex]

    // Make the default unit 5 spaces wide
    get(this, "dispatcher").dispatch("RESIZE_UNIT", {
      unitId: unitId,
      amount: newAmount,
      newDate: newEndDate.attributes.dateString,
      course: get(this, "course"),
      planbook: get(this, "planbook"),
      startOrEndOfUnit: "END",
      rotationCalendar: get(this, "rotationCalendar"),
    })
    let hydratedUnit = find(this.hydratedUnits, (unit) => unit.id == unitId)
    set(hydratedUnit, "contextMenuIsOpen", true)
  }

  @action
  addLesson(dateString) {
    this.modalManager.openModal("planner/course/timeline/sidebar", {
      dateString: dateString,
      courseId: this.course.id,
      planbookId: this.planbook.id,
      editCourseTitleAndOrder: this.editCourseTitleAndOrder,
      isUnit: false,
    })
  }

  @action
  onMouseEnterLesson(lessonId) {
    let request = get(this, "store").find("card-stack-summary", lessonId)
    request.then((cardStack) => {
      if (cardStack) {
        $(`#date-lesson-id-${lessonId}`)
          .attr({ "aria-label": get(cardStack, "attributes.title") })
          .addClass("hint hint--top")
      }
    })
  }
}
